import React from "react";
import { Order, OrderStatus } from "../../core/domain/entities/Order";
import { User, UserRole } from "../../core/domain/entities/User";
import "./OrderCard.css";

interface OrderCardProps {
  order: Order;
  currentUser: User;
  onOrderAction: (orderId: string, action: string) => void;
}

export const OrderCard: React.FC<OrderCardProps> = ({
  order,
  currentUser,
  onOrderAction,
}) => {
  const getStatusColor = (status: OrderStatus) => {
    switch (status) {
      case "pending_approval":
        return "#f59e0b";
      case "approved":
        return "#3b82f6";
      case "preparing":
        return "#8b5cf6";
      case "ready_for_shipping":
        return "#06b6d4";
      case "in_transit":
        return "#f97316";
      case "delivered":
        return "#10b981";
      case "cancelled":
        return "#ef4444";
      default:
        return "#6b7280";
    }
  };

  const getStatusText = (status: OrderStatus) => {
    switch (status) {
      case "pending_approval":
        return "Onay Bekliyor";
      case "approved":
        return "Onaylandı";
      case "preparing":
        return "Hazırlanıyor";
      case "ready_for_shipping":
        return "Sevkiyata Hazır";
      case "in_transit":
        return "Yolda";
      case "delivered":
        return "Teslim Edildi";
      case "cancelled":
        return "İptal Edildi";
      default:
        return status;
    }
  };

  const canApproveOrder = (userRole: UserRole) => {
    return userRole === "manager" || userRole === "admin";
  };

  const canPrepareOrder = (userRole: UserRole) => {
    return userRole === "warehouse" || userRole === "admin";
  };

  const canDeliverOrder = (userRole: UserRole) => {
    return userRole === "driver" || userRole === "admin";
  };

  const canCancelOrder = (userRole: UserRole) => {
    return userRole === "manager" || userRole === "admin";
  };

  return (
    <div className="order-card">
      <div className="order-header">
        <h3>Sipariş #{order.orderNumber}</h3>
        <span
          className="status-badge"
          style={{ backgroundColor: getStatusColor(order.status) }}
        >
          {getStatusText(order.status)}
        </span>
      </div>

      <div className="order-details">
        <p>
          <strong>Sipariş No:</strong>{" "}
          {order.orderNumber || order.order_number || order.id}
        </p>
        <p>
          <strong>Müşteri:</strong>{" "}
          {(order.customer as any)?.full_name ||
            order.customer?.name ||
            order.customer_name ||
            order.customerEmail ||
            order.customer_email ||
            (order.customer as any)?.contact_person ||
            "Bilinmeyen Müşteri"}
        </p>
        <p>
          <strong>Toplam:</strong> ₺
          {(() => {
            const total =
              order.grandTotal ||
              order.grand_total ||
              order.total_amount ||
              (order as any).totalAmount ||
              0;
            return total.toLocaleString();
          })()}
        </p>
        <p>
          <strong>Tarih:</strong>{" "}
          {order.createdAt || order.created_at
            ? new Date(order.createdAt || order.created_at!).toLocaleDateString(
                "tr-TR"
              )
            : "Tarih bilgisi yok"}
        </p>
        {order.deliveryDate && (
          <p>
            <strong>Teslimat:</strong>{" "}
            {new Date(order.deliveryDate).toLocaleDateString("tr-TR")}
          </p>
        )}
        {order.notes && (
          <p>
            <strong>Notlar:</strong> {order.notes}
          </p>
        )}
        {(order.customerPhone ||
          order.customer_phone ||
          (order.customer as any)?.phone) && (
          <p>
            <strong>Telefon:</strong>{" "}
            {order.customerPhone ||
              order.customer_phone ||
              (order.customer as any)?.phone}
          </p>
        )}
        {(order.customerAddress ||
          order.customer_address ||
          (order.customer as any)?.address) && (
          <p>
            <strong>Adres:</strong>{" "}
            {order.customerAddress ||
              order.customer_address ||
              (order.customer as any)?.address}
          </p>
        )}
        {order.salesperson_name && (
          <p>
            <strong>Satış Temsilcisi:</strong> {order.salesperson_name}
          </p>
        )}
        {/* Debug information - remove in production */}
        {process.env.NODE_ENV === "development" && (
          <details
            style={{ marginTop: "10px", fontSize: "12px", color: "#666" }}
          >
            <summary>Debug Info</summary>
            <pre>{JSON.stringify(order, null, 2)}</pre>
          </details>
        )}
      </div>

      <div className="order-actions">
        {currentUser &&
          canApproveOrder(currentUser.role) &&
          order.status === "pending_approval" && (
            <button
              className="action-button edit"
              onClick={() => onOrderAction(order.id, "approve")}
            >
              Onayla
            </button>
          )}
        {currentUser &&
          canPrepareOrder(currentUser.role) &&
          order.status === "approved" && (
            <button
              className="action-button edit"
              onClick={() => onOrderAction(order.id, "prepare")}
            >
              Hazırlamaya Başla
            </button>
          )}
        {currentUser &&
          canPrepareOrder(currentUser.role) &&
          order.status === "preparing" && (
            <button
              className="action-button edit"
              onClick={() => onOrderAction(order.id, "ready")}
            >
              Hazır
            </button>
          )}
        {currentUser &&
          canDeliverOrder(currentUser.role) &&
          order.status === "ready_for_shipping" && (
            <button
              className="action-button edit"
              onClick={() => onOrderAction(order.id, "ship")}
            >
              Yola Çık
            </button>
          )}
        {currentUser &&
          canDeliverOrder(currentUser.role) &&
          order.status === "in_transit" && (
            <button
              className="action-button edit"
              onClick={() => onOrderAction(order.id, "deliver")}
            >
              Teslim Et
            </button>
          )}
        {currentUser &&
          canCancelOrder(currentUser.role) &&
          order.status !== "cancelled" &&
          order.status !== "delivered" && (
            <button
              className="action-button delete"
              onClick={() => onOrderAction(order.id, "cancel")}
            >
              İptal Et
            </button>
          )}
        {currentUser &&
          (currentUser.role === "manager" || currentUser.role === "admin") &&
          order.status === "cancelled" && (
            <button
              className="action-button delete"
              onClick={() => onOrderAction(order.id, "delete")}
              style={{ backgroundColor: "#dc2626" }}
            >
              🗑️ Sil
            </button>
          )}
      </div>
    </div>
  );
};
